import React from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

type MessageType = 'error' | 'success' | 'warning' | 'info'

interface VariantStyle {
  wrapper: string
  closeColor: string
}

const variantStyles: Record<MessageType, VariantStyle> = {
  error: {
    wrapper: 'bg-purple-highlight border-[1.5px] border-purple-shade text-text',
    closeColor: 'text-text',
  },
  success: {
    wrapper: 'bg-mint-highlight border border-mint text-text',
    closeColor: 'text-text',
  },
  warning: {
    wrapper: 'bg-beige-shade border border-beige-shade text-text',
    closeColor: 'text-text',
  },
  info: {
    wrapper: 'bg-purple-highlight border-[1.5px] border-purple-shade text-text',
    closeColor: 'text-text',
  },
}

interface MessageBoxProps {
  type?: MessageType
  icon?: React.ReactNode
  message: string
  onClose?: () => void
  closable?: boolean
}

const MessageBox = ({ type = 'error', icon, message, onClose, closable = true }: MessageBoxProps): React.ReactElement | null => {
  const { t } = useTranslation()
  if (!message) return null

  const variant = variantStyles[type] || variantStyles.error

  return (
    <div
      data-testid="app-message"
      data-message-type={type}
      className={clsx(
        'px-4 pr-10 py-4 rounded-lg text-[0.95rem] my-4 relative shadow-sm',
        variant.wrapper
      )}
    >
      {icon && (
        <span className="mr-[0.1rem] relative top-1 inline-flex">
          {icon}
        </span>
      )}
      <span className="flex-1">{message}</span>
      {closable && onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label={t('close')}
          className={clsx(
            'absolute top-2 right-3 bg-transparent border-0 text-[1.2rem] cursor-pointer leading-none p-0',
            variant.closeColor
          )}
        >
          &times;
        </button>
      )}
    </div>
  )
}

export default MessageBox
